import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { SITE_URL } from "../../components/seo/Seo";
import PageShell, { PageHero, Plate, Steps } from "./PageShell";
import PricingV2 from "../components/PricingV2";
import { Arrow, Display, Eyebrow } from "../components/Primitives";
import { pickLang } from "../copy";
import { route } from "../paths";

/** Build-your-service explainer: base + add-ons, live price. */
const SurMesurePageV2: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const isFr = lang === "fr";

  const steps = isFr
    ? [
        { title: "Choisissez votre base", desc: "Standard pour vos sneakers du quotidien, Luxe pour le cuir et le daim, Enfant jusqu'à la pointure 35. Le nettoyage complet est toujours inclus.", meta: "Base" },
        { title: "Ajoutez vos options", desc: "Collecte & livraison à domicile (dès 15€), express 24h ou 48h, imperméabilisation (dès 2€ par paire), désodorisation renforcée… Cochez uniquement ce dont vous avez besoin.", meta: "Options" },
        { title: "Payez le juste prix", desc: "Le total se met à jour en direct pendant que vous composez. Vous payez votre base + vos options, rien de plus.", meta: "Prix en direct" },
      ]
    : [
        { title: "Pick your base", desc: "Standard for everyday sneakers, Luxury for leather and suede, Kids up to size 35. The complete clean is always included.", meta: "Base" },
        { title: "Add your options", desc: "Home pickup & delivery (from €15), 24h or 48h express, waterproofing (from €2 per pair), deep deodorizing… Tick only what you need.", meta: "Add-ons" },
        { title: "Pay the right price", desc: "The total updates live while you build. You pay your base + your add-ons, nothing more.", meta: "Live price" },
      ];

  const chips = isFr
    ? ["Collecte & livraison dès 15€", "Express 24h ou 48h", "Imperméabilisation dès 2€", "Désodorisation", "Et plus…"]
    : ["Pickup & delivery from €15", "Express 24h or 48h", "Waterproofing from €2", "Deodorizing", "And more…"];

  return (
    <PageShell
      seo={{
        title: isFr
          ? "Nettoyage de baskets sur mesure : base + options | Lave Tes Baskets"
          : "Custom sneaker cleaning: base + add-ons | Lave Tes Baskets",
        description: isFr
          ? "Choisissez Standard, Luxe ou Enfant, ajoutez collecte & livraison, express 24h ou 48h, imperméabilisation. Le prix se calcule en direct, dès 20€ la paire."
          : "Pick Standard, Luxury or Kids, add pickup & delivery, 24h or 48h express, waterproofing. The price computes live, from €20 per pair.",
        canonicalPath: route(lang, "sur-mesure"),
        jsonLd: [
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: isFr ? "Comment composer son nettoyage sur mesure" : "How to build your custom cleaning",
            step: steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.desc })),
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: isFr ? "Nettoyage de sneakers sur mesure" : "Custom sneaker cleaning",
            provider: { "@type": "LocalBusiness", name: "Lave Tes Baskets" },
            areaServed: "Île-de-France",
            url: `${SITE_URL}${route(lang, "sur-mesure")}`,
          },
        ],
      }}
    >
      <PageHero
        crumbs={[
          { label: isFr ? "Accueil" : "Home", to: route(lang) },
          { label: isFr ? "Sur mesure" : "Custom care" },
        ]}
        index="05"
        eyebrow={isFr ? "Sur mesure" : "Custom care"}
        title={
          isFr ? (
            <>
              Une base, <em className="italic text-tangerine">vos options.</em>
            </>
          ) : (
            <>
              One base, <em className="italic text-tangerine">your add-ons.</em>
            </>
          )
        }
        lede={
          isFr
            ? "Comme un menu : choisissez votre base, cochez vos options, le prix se calcule en direct et s'affiche avant paiement."
            : "Like a menu: pick your base, tick your add-ons, the price computes live and shows before payment."
        }
        actions={
          <>
            <Link to={`/${lang}/dashboard`} className="v2-btn v2-btn-solid">
              {isFr ? "Composer ma prestation" : "Build my service"}
              <Arrow />
            </Link>
            <Link to={route(lang, "devis")} className="v2-btn v2-btn-ghost">
              {isFr ? "Besoin spécifique ? Devis photo" : "Something specific? Photo estimate"}
            </Link>
          </>
        }
        aside={
          <img src="/illustrations/landing/composer.webp" alt="" loading="lazy" className="ml-auto w-full max-w-[440px] object-contain" />
        }
      />

      <section className="bg-bone py-20 text-ink sm:py-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Eyebrow index="01">{isFr ? "Le principe" : "The idea"}</Eyebrow>
          <Display className="mt-5 max-w-3xl text-[clamp(2.2rem,5.5vw,5rem)]">
            {isFr ? "Vous composez, on exécute." : "You build, we execute."}
          </Display>
          <div className="mt-12">
            <Steps steps={steps} />
          </div>
          <div className="v2-reveal mt-14 flex flex-wrap items-center gap-2" data-delay="2">
            <span className="mr-3 font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">
              {isFr ? "Options disponibles" : "Available add-ons"}
            </span>
            {chips.map((c) => (
              <span key={c} className="rounded-full border border-ink/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em]">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="v2-hair border-t bg-ink pt-20 text-bone sm:pt-28">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Eyebrow index="02">{isFr ? "Les bases" : "The bases"}</Eyebrow>
          <Display className="mt-5 max-w-3xl text-[clamp(2.2rem,5.5vw,5rem)]">
            {isFr ? "Trois points de départ." : "Three starting points."}
          </Display>
        </div>
      </section>
      <PricingV2 hideHeader />

      <section className="bg-ink pb-16 text-bone sm:pb-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
          <Plate
            tone="teal"
            title={isFr ? "Prêt à composer votre prestation ?" : "Ready to build your service?"}
            sub={isFr ? "Créez votre compte et réservez en 2 minutes. Le prix s'affiche en direct." : "Create your account and book in 2 minutes. The price shows live."}
            img="/illustrations/landing/composer.webp"
            cta={
              <Link to={`/${lang}/dashboard`} className="v2-btn v2-btn-solid">
                {isFr ? "Composer ma prestation" : "Build my service"}
                <Arrow />
              </Link>
            }
          />
        </div>
      </section>
    </PageShell>
  );
};

export default SurMesurePageV2;
